import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react'

export type FileDropHandler = (files: File[]) => void

interface FileDropContextValue {
  register: (handler: FileDropHandler | null) => void
}

const FileDropContext = createContext<FileDropContextValue | null>(null)

export interface FileDropProviderProps {
  children: ReactNode
  /** Overlay shown while files are dragged anywhere over the window. */
  overlay?: ReactNode
}

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')

/**
 * Site-wide file-drop handling. Listens on the window and dispatches dropped
 * files to whatever handler the active page registered via `useGlobalFileDrop`,
 * so a drop can be interpreted differently per page. Shows `overlay` (or a
 * default) while a drag is in progress.
 */
export function FileDropProvider({ children, overlay }: FileDropProviderProps) {
  const handlerRef = useRef<FileDropHandler | null>(null)
  const dragDepth = useRef(0)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return
      dragDepth.current += 1
      setDragging(true)
    }
    const onOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault()
    }
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDragging(false)
    }
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      const files = Array.from(e.dataTransfer?.files ?? [])
      if (files.length) handlerRef.current?.(files)
    }

    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragover', onOver)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  const register = useRef<FileDropContextValue['register']>((handler) => {
    handlerRef.current = handler
  }).current

  return (
    <FileDropContext.Provider value={{ register }}>
      {children}
      {dragging ? (overlay ?? <div className="aug-file-drop-overlay">Drop files to import</div>) : null}
    </FileDropContext.Provider>
  )
}

/**
 * Register a handler for files dropped anywhere on the page. The most recent
 * caller wins, so the active page/module controls how a drop is interpreted.
 * The handler is unregistered on unmount.
 */
export function useGlobalFileDrop(handler: FileDropHandler): void {
  const ctx = useContext(FileDropContext)
  if (!ctx) throw new Error('useGlobalFileDrop must be used within <FileDropProvider>')

  // Keep the latest handler without re-registering on every render.
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    ctx.register((files) => handlerRef.current(files))
    return () => ctx.register(null)
  }, [ctx])
}
